import { freeBetRewardWrites } from "helpers/sportsbookRewardMechanics";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalBonusTabs, ModalBonusSettingsTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  getBonusTypesList,
  getBonusStatusList,
  getWagerList,
  handleClearFields,
  handleUpdateFields,
  getBonusesItem,
  createBonus,
  updateBonus,
  setRewards,
  setRules,
  setBets,
  setBonusTranslation,
  getBonusTranslation,
  handlePaginate,
} from "store/liveOps/bonuses/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import BonusesMainTab from "../tabs/bonuses/BonusesMainTab";
import BonusesSettingsTab from "../tabs/bonuses/BonusesSettingsTab";
import BonusesTimingTab from "../tabs/bonuses/BonusesTimingTab";
import BonusesContentTab from "../tabs/bonuses/BonusesContentTab";

import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsBonusesEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import {
  BONUS_SCHEDULE_FIELDS,
  bonusScheduleFieldErrors,
  findBonusTypeTitle,
} from "helpers/bonusScheduleTypeData";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import cn from "clsx";

const isPositiveFixedDecimal = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const normalized = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized) || !/[1-9]/.test(normalized)) {
    return false;
  }
  const [integerPart, fractionalPart = ""] = normalized.split(".");
  return (
    integerPart.replace(/^0+/, "").length <= 18 && fractionalPart.length <= 18
  );
};

const LiveOpsBonusesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const permissions = usePermissionHelper();
  const isExistingBonus = entityId !== null;
  const canSave = isExistingBonus
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);
  const isReadOnly = isExistingBonus && !permissions?.update;

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const isItemFetched = useAppSelector((store) => store.bonuses.isItemFetched);
  const isListTypesFetched = useAppSelector(
    (store) => store.bonuses.isListTypesFetched,
  );
  const isListStatusFetched = useAppSelector(
    (store) => store.bonuses.isListStatusFetched,
  );
  const isListWagerFetched = useAppSelector(
    (store) => store.bonuses.isListWagerFetched,
  );
  const payload = useAppSelector((store) => store.bonuses.payload);
  const typesList = useAppSelector((store) => store.bonuses.typesList);
  const translations = useAppSelector((store) => store.bonuses.translations);
  const locales = useAppSelector((store) => store.settings.locales);
  const isItemFetching = useAppSelector(
    (store) => store.bonuses.isItemFetching,
  );

  const [tabs] = useState<ModalBonusTabs[]>([
    ModalBonusTabs.main,
    ModalBonusTabs.settings,
    ModalBonusTabs.timing,
    ModalBonusTabs.locales,
  ]);
  const [activeTab, setActiveTab] = useState<ModalBonusTabs>(
    ModalBonusTabs.main,
  );
  const [tabsSettings] = useState<ModalBonusSettingsTabs[]>([
    ModalBonusSettingsTabs.activationRules,
    ModalBonusSettingsTabs.rewards,
    ModalBonusSettingsTabs.betsRange,
  ]);
  const [activeTabSettings, setActiveTabSettings] =
    useState<ModalBonusSettingsTabs>(ModalBonusSettingsTabs.activationRules);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [savedCurrencies, setSavedCurrencies] = useState<string[]>([]);
  const [activeCurrency, setActiveCurrency] = useState<string>("");
  const currentItem = payload?.additionalConfigs.filter(
    (el) => el?.currency === activeCurrency,
  )[0];
  const [locale, setLocale] = useState<string>(locales[0]);
  const isLocaleLoading =
    activeTab === ModalBonusTabs.locales && !translations[locale];

  useEffect(() => {
    if (payload.id) {
      dispatch(getBonusTranslation({ bonusId: payload.id, locale }));
    }
  }, [locale, payload.id]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [locale]);

  useEffect(() => {
    if (!isListTypesFetched) {
      dispatch(getBonusTypesList());
    }
    if (!isListStatusFetched) {
      dispatch(getBonusStatusList());
    }
    if (!isListWagerFetched) {
      dispatch(getWagerList());
    }
  }, [isListTypesFetched, isListStatusFetched, isListWagerFetched]);

  useEffect(() => {
    if (selectedCurrencies.length > 0) {
      setActiveCurrency(selectedCurrencies[0]);
    }
  }, [selectedCurrencies]);

  useEffect(() => {
    if (entityId !== null && !isItemFetched) {
      dispatch(getBonusesItem(entityId));
    } else if (entityId !== null && isItemFetched) {
      setSelectedCurrencies(payload.currencies);
      setSavedCurrencies(payload.currencies);
    }
  }, [entityId, isItemFetched]);

  const formErrors = useFormErrors();
  const isEdit = Boolean(entityId || payload.id);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleTabClick = (e: ModalBonusTabs) => {
    if (!isEdit && e !== ModalBonusTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(e);
  };
  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const renderTab = () => {
    if (activeTab === ModalBonusTabs.main) {
      return (
        <BonusesMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={isReadOnly}
        />
      );
    }
    if (activeTab === ModalBonusTabs.settings) {
      return (
        <BonusesSettingsTab
          currency={activeCurrency}
          activeTab={activeTabSettings}
          tabs={tabsSettings}
          setActiveTab={setActiveTabSettings}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
        />
      );
    }
    if (activeTab === ModalBonusTabs.timing) {
      return (
        <BonusesTimingTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
        />
      );
    }
    if (activeTab === ModalBonusTabs.locales) {
      return (
        <BonusesContentTab
          activeLocale={locale}
          setActiveLocale={setLocale}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={isReadOnly}
        />
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  const handleApply = async () => {
    if (!canSave) return;

    if (selectedCurrencies.length === 0) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("currencyIsRequired"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    if (activeTab === ModalBonusTabs.locales && payload?.id) {
      try {
        await dispatch(
          setBonusTranslation({
            bonusId: payload.id,
            payload: {
              ...translations[locale],
              locale: locale,
            },
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        dispatch(handlePaginate(1));
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (
      currentItem &&
      activeTab === ModalBonusTabs.settings &&
      activeTabSettings === ModalBonusSettingsTabs.activationRules
    ) {
      try {
        await dispatch(
          setRules({
            cancelAmount: currentItem.activations.find(
              (el) => el.type === "cancellationAmount",
            )?.min,
            depositRequired: currentItem.activations.find(
              (el) => el.type === "noDeposit",
            )
              ? 0
              : 1,
            numberOfDeposit: currentItem.activations.find(
              (el) => el.type === "deposit",
            )?.numberOfDeposit,
            min: currentItem.activations.find((el) => el.type === "deposit")
              ?.min,
            max: currentItem.activations.find((el) => el.type === "deposit")
              ?.max,
            id: searchParams.get("entityId") ?? "",
            currency: activeCurrency,
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        dispatch(handlePaginate(1));
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
      return;
    }

    if (
      currentItem &&
      activeTab === ModalBonusTabs.settings &&
      activeTabSettings === ModalBonusSettingsTabs.rewards
    ) {
      let freeBetWrites: { type: string; config?: string }[];
      try {
        freeBetWrites = freeBetRewardWrites(currentItem.rewards, activeCurrency);
      } catch {
        formErrors.setFieldError("sportsbookTemplate", "sportsbookTemplateMismatch");
        return;
      }
      const comboBoostReward = currentItem.rewards.find(
        (el) => el.type === "comboBoost",
      );
      const pendingComboBoostDelete =
        currentItem.pendingRewardDeletes?.includes("comboboost");
      const bonusBalanceReward = currentItem.rewards.find(
        (el) => el.type === "bonusBalance",
      );
      const pendingBonusBalanceDelete =
        currentItem.pendingRewardDeletes?.includes("bonusBalance");

      if (
        comboBoostReward &&
        !comboBoostReward.templateId &&
        !pendingComboBoostDelete
      ) {
        formErrors.setFieldError("comboBoost", "requiredField");
        return;
      }
      if (
        bonusBalanceReward &&
        !pendingBonusBalanceDelete &&
        !isPositiveFixedDecimal(bonusBalanceReward.value)
      ) {
        formErrors.setFieldError("bonusBalance", "requiredField");
        return;
      }

      const rewardsPayload: { type: string; config?: string }[] = [...freeBetWrites];

      if (pendingComboBoostDelete) {
        rewardsPayload.push({ type: "comboboost" });
      } else if (comboBoostReward?.templateId) {
        rewardsPayload.push({
          type: "comboboost",
          config: JSON.stringify({
            ...comboBoostReward.persistedConfig,
            templateId: String(comboBoostReward.templateId),
            providerTemplate: comboBoostReward.providerTemplate,
          }),
        });
      }
      if (pendingBonusBalanceDelete) {
        rewardsPayload.push({ type: "bonusBalance" });
      } else if (bonusBalanceReward?.value) {
        rewardsPayload.push({
          type: "bonusBalance",
          config: JSON.stringify({
            value: bonusBalanceReward.value.trim(),
          }),
        });
      }

      try {
        await dispatch(
          setRewards({
            depositPercentage: currentItem.rewards.find(
              (el) => el.type === "depositPercentage",
            )?.value,
            depositPercentageMax: currentItem.rewards.find(
              (el) => el.type === "depositPercentage",
            )?.max,
            depositStatic: currentItem.rewards.find(
              (el) => el.type === "depositStatic",
            )?.value,
            coins: currentItem.rewards.find((el) => el.type === "coins")
              ?.points,
            loyalty: currentItem.rewards.find((el) => el.type === "loyalty")
              ?.points,
            ranking: currentItem.rewards.find((el) => el.type === "ranking")
              ?.points,
            freespins: currentItem.rewards.find((el) => el.type === "freespins")
              ? {
                  gameId: currentItem.rewards.find(
                    (el) => el.type === "freespins",
                  )?.gameId,
                  providerId: currentItem.rewards.find(
                    (el) => el.type === "freespins",
                  )?.providerId,
                  totalAmount: currentItem.rewards.find(
                    (el) => el.type === "freespins",
                  )?.totalAmount,
                  spinAmount: currentItem.rewards.find(
                    (el) => el.type === "freespins",
                  )?.spinAmount,
                  maxWin: currentItem.rewards.find(
                    (el) => el.type === "freespins",
                  )?.maxWin,
                  winningsDestination:
                    currentItem.rewards.find((el) => el.type === "freespins")
                      ?.winningsDestination === "real"
                      ? "real"
                      : "bonus",
                }
              : undefined,
            rewards: rewardsPayload.length > 0 ? rewardsPayload : undefined,
            id: searchParams.get("entityId") ?? "",
            currency: activeCurrency,
          }),
        ).unwrap();

        if (
          pendingComboBoostDelete ||
          comboBoostReward ||
          pendingBonusBalanceDelete ||
          bonusBalanceReward
        ) {
          dispatch(
            handleUpdateFields({
              additionalConfigs: payload.additionalConfigs.map((config) =>
                config.currency === activeCurrency
                  ? {
                      ...config,
                      pendingRewardDeletes: (
                        config.pendingRewardDeletes ?? []
                      ).filter(
                        (rewardType) =>
                          rewardType !== "comboboost" &&
                          rewardType !== "bonusBalance",
                      ),
                    }
                  : config,
              ),
            }),
          );
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        dispatch(handlePaginate(1));
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (
      currentItem &&
      activeTab === ModalBonusTabs.settings &&
      activeTabSettings === ModalBonusSettingsTabs.betsRange
    ) {
      try {
        await dispatch(
          setBets({
            betsRangeMax: currentItem.betsRangeMax,
            betsRangeMin: currentItem.betsRangeMin,
            id: searchParams.get("entityId") ?? "",
            currency: activeCurrency,
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        dispatch(handlePaginate(1));
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (
      payload !== undefined &&
      (activeTab === ModalBonusTabs.main || activeTab === ModalBonusTabs.timing)
    ) {
      formErrors.clearFieldsErrors([...BONUS_SCHEDULE_FIELDS]);
      const scheduleErrors = bonusScheduleFieldErrors(
        findBonusTypeTitle(typesList, payload.typeId),
        payload,
      );
      if (Object.keys(scheduleErrors).length > 0) {
        Object.entries(scheduleErrors).forEach(([field, code]) => {
          formErrors.setFieldError(field, code);
        });
        setActiveTab(ModalBonusTabs.timing);
        return;
      }

      if (searchParams.get("entityId") !== null) {
        try {
          await dispatch(updateBonus(payload)).unwrap();

          const addedCurrencies = selectedCurrencies.filter(
            (currency) => !savedCurrencies.includes(currency),
          );

          for (const currency of addedCurrencies) {
            await dispatch(
              setRules({
                id: searchParams.get("entityId")!,
                currency,
                depositRequired: 0,
              }),
            ).unwrap();
          }

          setSavedCurrencies(selectedCurrencies);

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
        } catch (error) {
          handleApiError({
            error,
            t,
            dispatch,
            formErrors,
          });
        }
      } else {
        try {
          const newId = await dispatch(createBonus(payload)).unwrap();

          for (const currency of payload.currencies) {
            await dispatch(
              setRules({
                id: String(newId),
                currency,
                depositRequired: 0,
              }),
            );
          }
          dispatch(handlePaginate(1));

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );

          navigate(`${location.pathname}?activeModal=bonus&entityId=${newId}`);
        } catch (error) {
          handleApiError({
            error,
            t,
            dispatch,
            formErrors,
          });
        }
      }
    }
  };

  const handleCurrencyTabClick = (tab: string) => {
    if (!canSave) return;

    const isRemoving = selectedCurrencies.includes(tab);

    const updatedCurrencies = isRemoving
      ? selectedCurrencies.filter((c) => c !== tab)
      : [...selectedCurrencies, tab];

    setSelectedCurrencies(updatedCurrencies);

    dispatch(
      handleUpdateFields({
        currencies: updatedCurrencies,
      }),
    );

    if (
      !isRemoving &&
      !payload.additionalConfigs.some((cfg) => cfg.currency === tab)
    ) {
      dispatch(
        handleUpdateFields({
          additionalConfigs: [
            ...payload.additionalConfigs,
            {
              currency: tab,
              betsRangeMin: 0,
              betsRangeMax: 0,
              activations: [{ type: "noDeposit", currency: tab }],
              rewards: [],
            },
          ],
        }),
      );
    }
  };

  if (
    permissions &&
    (!permissions.read || (!isExistingBonus && !permissions.create))
  ) {
    return null;
  }

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("bonus")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={isEdit}
            onClick={handleTabClick}
          />

          {activeTab === ModalBonusTabs.main && (
            <Modal.Tabs
              tabs={currenciesList}
              activeTab={selectedCurrencies}
              onClick={handleCurrencyTabClick}
              isCurrencyTabs
              isCurrencyTabsCheckbox={!isReadOnly}
              className={cn(
                styles.liveopsBonusesEditModalCurrencyTabs,
                styles.liveopsBonusesEditModalMainCurrencyTabs,
              )}
            />
          )}

          {activeTab === ModalBonusTabs.settings && (
            <Modal.Tabs
              tabs={selectedCurrencies}
              activeTab={activeCurrency}
              onClick={(currency) => {
                setActiveCurrency(currency);
                formErrors.resetErrors();
              }}
              isCurrencyTabs
              className={styles.liveopsBonusesEditModalCurrencyTabs}
            />
          )}

          <Modal.Content
            className={cn(
              styles.liveopsBonusesEditModalContent,
              isReadOnly && styles.liveopsBonusesEditModalContentReadOnly,
            )}
          >
            <fieldset
              className={styles.liveopsBonusesEditModalFieldset}
              disabled={isReadOnly}
              aria-disabled={isReadOnly}
            >
              <div className="bonus-content">{renderTab()}</div>
            </fieldset>
          </Modal.Content>

          {canSave && (
            <Modal.Footer>
              {isLocaleLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    txt={t("save")}
                    handleClick={handleApply}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsBonusesEditModal;
